import { JSX, MouseEvent, MouseEventHandler, useState, useEffect } from "react";
import { QueryChanger, usePageQuery } from "../../shared/hooks/usePageQuery";
import {
  PagedComponentState,
  usePagedComponentStateMachine,
} from "../../shared/hooks/usePageComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { ErrorView } from "../../components/error-view/ErrorView";
import { LoadingView } from "../../components/loading-view/LoadingView";
import FunctionalList, {
  TableData,
} from "../../components/GenericTable/FunctionalList";
import { Manufacturer } from "../../../core/models/entities/Manufacturer";
import { PageQuery } from "../../../utils/models/pageQuery";
import { useNavigate } from "react-router-dom";
import { ColumnSettings } from "../../components/GenericTable/SubComponents/ListFrame";
import { ColumnField } from "../../components/GenericTable/SubComponents/Column";
import { StrucRow } from "../../components/StructureElements/StrucRow";
import { StrucCol } from "../../components/StructureElements/StrucCol";
import { nameOf } from "../../../utils/functions/ReflectionUtil";
import { Tooltip } from "@mui/material";
import IconButton from "../../components/LogoButton/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { SortDirection } from "../../components/GenericTable/Models/sortDirection";
import { useTranslation } from "react-i18next";

export function ManufacturerListScreen(): JSX.Element {
  const [query, queryChanger] = usePageQuery();

  // Set default sort to A-Z by manufacturer name
  useEffect(() => {
    if (!query.sortBy?.field) {
      queryChanger.setPageQuery({
        ...query,
        sortBy: {
          field: "name",
          sortDirection: SortDirection.ASC,
        },
      });
    }
  }, []);
  const { state, dataTransition } = usePagedComponentStateMachine();
  const { manufacturerRepository } = useRepositoriesContext();

  const [lastPage, setLastPage] = useState<number>(1);

  const {
    data: manufacturers,
    isSuccess: isManufacturersSuccess,
    isLoading: isManufacturersLoading,
    isError: isManufacturersError,
  } = manufacturerRepository.useManufacturers();

  if (manufacturers && manufacturers.length > 0 && lastPage !== 1) {
    setLastPage(1);
  }

  const isLoading = isManufacturersLoading;
  const isSuccess = isManufacturersSuccess;
  const isError = isManufacturersError;
  dataTransition(isLoading, isSuccess, isError);

  return state === PagedComponentState.Succeed ||
    state === PagedComponentState.PageLoading ? (
    <ManufacturerListWrapper
      manufacturers={manufacturers}
      isPageLoading={state === PagedComponentState.PageLoading}
      query={query}
      queryChanger={queryChanger}
      lastPage={lastPage}
    />
  ) : state === PagedComponentState.Failed ? (
    <ErrorView />
  ) : (
    <LoadingView />
  );
}

interface WrapperProps {
  manufacturers?: Manufacturer[];
  isPageLoading: boolean;
  query: PageQuery;
  queryChanger: QueryChanger;
  lastPage: number;
}

export function ManufacturerListWrapper({
  manufacturers,
  isPageLoading,
  query,
  queryChanger,
  lastPage,
}: WrapperProps) {
  const vm = useFunctionalListManufacturerViewModel(
    isPageLoading,
    query,
    queryChanger,
    lastPage,
    manufacturers
  );
  return <FunctionalList<Manufacturer> {...vm} />;
}

export function useFunctionalListManufacturerViewModel(
  isLoading: boolean,
  query: PageQuery,
  queryChanger: QueryChanger,
  lastPage: number,
  manufacturers?: Manufacturer[]
): TableData<Manufacturer> {
  const navigate = useNavigate();
  const { currency } = useProfileContext();
  const { t } = useTranslation();

  function handleRowClick(manufacturer: Manufacturer) {
    navigate(`/manufacturer/${manufacturer.id}`);
  }

  // Filter out manufacturers with empty names
  const filteredManufacturers =
    manufacturers?.filter((manu) => manu.name && manu.name.trim() !== "") || [];

  const columnLabelKeys: Record<string, string> = {
    [nameOf<Manufacturer>((manu) => manu.id)]: "orgScreens.columnId",
    [nameOf<Manufacturer>((manu) => manu.name)]: "name",
    [nameOf<Manufacturer>((manu) => manu.isRestricted)]:
      "orgScreens.columnRestricted",
  };

  const columns = manufacturerColumnsConfig.map((column) => ({
    ...column,
    displayValue: columnLabelKeys[column.value]
      ? t(columnLabelKeys[column.value])
      : column.displayValue,
  }));

  return {
    data: filteredManufacturers,
    query: query,
    lastPage: lastPage,
    isLoading: isLoading,
    tableTitle: t("manufacturers"),
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    multiSelect: false,
    columns: columns,
    onNewSort: queryChanger.handleSortByField,
    onPageSizeChange: queryChanger.handlePageSizeChange,
    onNewPage: queryChanger.handleNewPage,
    renderRow: (columns, data) => (
      <ManufacturerListItemComponent
        manufacturer={data}
        currency={currency}
        columns={columns}
      />
    ),
    onRowClick: handleRowClick,
    getKey: (d) => d.id,
  };
}

export interface ManufacturerListItemProps {
  manufacturer: Manufacturer;
  columns: ColumnSettings;
  currency: string;
}

export function ManufacturerListItemComponent({
  manufacturer,
  columns,
  currency,
}: ManufacturerListItemProps) {
  const { t } = useTranslation();
  return (
    <StrucRow className={"py-4"}>
      <StrucCol size={columns[nameOf<Manufacturer>((manu) => manu.id)].weight}>
        <Tooltip title={manufacturer.id.toString()} placement={"top-start"}>
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
          >
            {manufacturer.id}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol
        size={columns[nameOf<Manufacturer>((manu) => manu.name)].weight}
      >
        <Tooltip title={manufacturer.name} placement={"top-start"}>
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
          >
            {manufacturer.name}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol
        size={columns[nameOf<Manufacturer>((manu) => manu.isRestricted)].weight}
      >
        <Tooltip
          title={
            manufacturer.isRestricted
              ? t("orgScreens.restricted")
              : t("orgScreens.notRestricted")
          }
          placement={"top-start"}
        >
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
          >
            {manufacturer.isRestricted ? t("orgScreens.yes") : t("orgScreens.no")}
          </span>
        </Tooltip>
      </StrucCol>
    </StrucRow>
  );
}

// Manufacturer columns configuration
const manufacturerColumnsConfig: ColumnField[] = [
  {
    value: nameOf<Manufacturer>((manu) => manu.id),
    displayValue: "ID",
    weight: 1,
    canSortBy: true,
  },
  {
    value: nameOf<Manufacturer>((manu) => manu.name),
    displayValue: "Name",
    weight: 6,
    canSortBy: true,
  },
  {
    value: nameOf<Manufacturer>((manu) => manu.isRestricted),
    displayValue: "Restricted",
    weight: 3,
    canSortBy: true,
  },
];
